import React, { useState } from 'react';
import { Group, Rect, Text } from 'react-konva';
import { ThemePalette } from '../../types';
import { FONT_FAMILY } from '../../constants/typography';

interface NodeActionBarProps {
  x: number;
  y: number;
  nodeId: string;
  isGroup: boolean;
  hasLinkedPage: boolean;
  palette: ThemePalette;
  accentColor: string;
  onInfo: () => void;
  onColor: () => void;
  onEnterGroup?: () => void;
  onPortalNavigate?: () => void;
  onLinkPage: () => void;
}

const BTN_SIZE = 16;
const BTN_GAP = 2;
const BTN_RADIUS = 4;
const PADDING = 4;
/** Height of the bar — used to park it just outside the node’s top edge. */
export const NODE_ACTION_BAR_HEIGHT = BTN_SIZE + PADDING * 2;

export const NodeActionBar: React.FC<NodeActionBarProps> = ({
  x, y, // eslint-disable-next-line @typescript-eslint/no-unused-vars
  nodeId: _nodeId,
  isGroup, hasLinkedPage,
  palette, accentColor,
  onInfo, onColor,
  onEnterGroup, onPortalNavigate, onLinkPage,
}) => {
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const buttons: Array<{
    id: string;
    label: string;
    icon: string;
    color: string;
    onClick: () => void;
    alwaysVisible?: boolean;
  }> = [
    { id: 'info', label: 'Info', icon: 'ℹ', color: palette.panelSubText, onClick: onInfo },
    { id: 'color', label: 'Color', icon: '◕', color: accentColor, onClick: onColor },
    ...(isGroup && onEnterGroup ? [{
      id: 'enter',
      label: 'Enter group (Tab)',
      icon: '▶',
      color: '#818cf8',
      onClick: onEnterGroup,
    }] : []),
    ...(hasLinkedPage && onPortalNavigate ? [{
      id: 'portal-go',
      label: 'Go to linked page',
      icon: '⇒',
      color: '#10b981',
      onClick: onPortalNavigate,
      alwaysVisible: true,
    }] : []),
    { id: 'link-page', label: 'Link to page', icon: '⊞', color: palette.panelSubText, onClick: onLinkPage },
  ];

  const totalW = buttons.length * (BTN_SIZE + BTN_GAP) - BTN_GAP + PADDING * 2;
  const totalH = NODE_ACTION_BAR_HEIGHT;
  const gap = 4;

  // (x, y) is the node’s top-right. Sit just above that corner so the bar
  // stays outside the body. An invisible strip covers the gap so hover isn't lost.
  return (
    <Group x={x - totalW} y={y - totalH - gap}>
      <Rect
        y={totalH}
        width={totalW}
        height={gap}
        fill="transparent"
        perfectDrawEnabled={false}
      />
      {/* Background pill */}
      <Rect
        width={totalW}
        height={totalH}
        fill={palette.panelBg}
        stroke={palette.panelBorder}
        strokeWidth={0.8}
        cornerRadius={BTN_RADIUS + PADDING}
        opacity={0.95}
        perfectDrawEnabled={false}
      />

      {/* Buttons */}
      {buttons.map((btn, i) => {
        const bx = PADDING + i * (BTN_SIZE + BTN_GAP);
        const by = PADDING;
        const isHovered = hoveredBtn === btn.id;

        return (
          <Group key={btn.id} x={bx} y={by}>
            <Rect
              width={BTN_SIZE}
              height={BTN_SIZE}
              fill={isHovered ? `${btn.color}30` : 'transparent'}
              cornerRadius={BTN_RADIUS}
              onClick={(e) => { e.cancelBubble = true; btn.onClick(); }}
              onMouseEnter={(e) => {
                setHoveredBtn(btn.id);
                const c = e.target.getStage()?.container();
                if (c) c.style.cursor = 'pointer';
              }}
              onMouseLeave={(e) => {
                setHoveredBtn(null);
                const c = e.target.getStage()?.container();
                if (c) c.style.cursor = 'default';
              }}
              perfectDrawEnabled={false}
            />
            <Text
              x={0} y={0}
              width={BTN_SIZE}
              height={BTN_SIZE}
              text={btn.icon}
              fontSize={10}
              fontFamily={FONT_FAMILY}
              fill={isHovered ? btn.color : `${btn.color}cc`}
              align="center"
              verticalAlign="middle"
              listening={false}
              perfectDrawEnabled={false}
            />
          </Group>
        );
      })}
    </Group>
  );
};
